import { z } from 'zod';

/**
 * Whether a match is still standing.
 *
 * Two states, not a machine: a match is created by agreement and stops for a
 * reason, and nothing in between is a state anybody acts on differently.
 *
 * - `active` — both members agreed and neither has withdrawn. Chat exists only
 *   for these (N13), and only these resolve a viewer to the `matched` context
 *   (N18).
 * - `ended` — it stopped. **Why** it stopped is {@link MatchEndReasonSchema},
 *   and it is a separate field on purpose: "she unmatched me" and "the account
 *   was closed" are the same status and very different facts, and a status that
 *   encoded both would have to grow a value for every future reason.
 */
export const MATCH_STATUSES = ['active', 'ended'] as const;

export const MatchStatusSchema = z.enum(MATCH_STATUSES);

export type MatchStatus = (typeof MATCH_STATUSES)[number];

/** A match comes into existence because two people agreed; there is no draft. */
export const INITIAL_MATCH_STATUS: MatchStatus = 'active';

/**
 * The status "my matches" means.
 *
 * **Deliberately a second constant with the same value as
 * {@link INITIAL_MATCH_STATUS}, and they must not be merged.** They are two
 * different facts that happen to coincide today: one says what a match is
 * created as, the other says which matches a member is shown by default. Using
 * the creation constant for the list default reads fine and is a latent bug —
 * the day a third status is introduced ahead of `active`, the list would
 * silently follow the *creation* state and start showing members something else
 * entirely. One spelling per fact.
 */
export const LIVE_MATCH_STATUS: MatchStatus = 'active';

/**
 * The match is still standing.
 *
 * The **partial unique index** that enforces "one live match per pair" is built
 * from exactly this predicate, so the index and this function are one fact with
 * two spellings — the same discipline `isOpenCaseStatus` keeps for verification
 * cases. A status counted live here and omitted there would let a pair
 * accumulate matches.
 */
export function isActiveMatchStatus(status: MatchStatus): boolean {
  return status === 'active';
}

/**
 * Why a match stopped.
 *
 * `unmatched` is a member's own withdrawal; `blocked` is the moderation side
 * effect P19 writes; `account_closed` covers a ban, a suspension that outlives
 * the match, or an in-app deletion. None of them is written by this phase — the
 * column exists so the phase that writes one does not have to migrate the table
 * to say why.
 */
export const MATCH_END_REASONS = ['unmatched', 'blocked', 'account_closed'] as const;

export const MatchEndReasonSchema = z.enum(MATCH_END_REASONS);

export type MatchEndReason = (typeof MATCH_END_REASONS)[number];
